import {LitElement, html, css} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {iconCheck, iconX} from '../../utils/icons.js';

export interface StepItem {
	id: string;
	label: string;
	sublabel?: string;
}

export interface StepChunkResult {
	id: string;
	ok: boolean;
	error?: string;
}

export type StepPhase = 'idle' | 'running' | 'done' | 'cancelled';
type ItemStatus = 'pending' | 'sending' | 'ok' | 'err';

export interface StepProgressDoneDetail {
	sent: number;
	failed: number;
	total: number;
	cancelled: boolean;
}

/**
 * Modal reutilizable para ops largas cliente-side con feedback paso a paso.
 *
 * Uso típico:
 *   <clam-step-progress
 *     .open=${this._progressOpen}
 *     .items=${items}
 *     .onChunk=${(ids) => callBackend(ids)}
 *     chunkSize="25"
 *     title="Enviando correos"
 *     @clam-step-progress-done=${this._onDone}
 *     @clam-step-progress-close=${this._onClose}
 *   ></clam-step-progress>
 *
 * El padre controla `open`. Al pasar de false → true arranca el loop.
 * `onChunk` recibe un subconjunto de ids (chunkSize) y devuelve resultados por id.
 * El loop corta entre chunks si el usuario presiona Cancelar.
 */
@customElement('clam-step-progress')
export class ClamStepProgress extends LitElement {
	static styles = css`
		:host { display: contents; font-family: var(--clam-font); color: var(--clam-text); }
		.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.55); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: var(--clam-space-4); }
		.modal { background: var(--clam-surface); border-radius: var(--clam-radius-lg); max-width: 600px; width: 100%; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: var(--clam-shadow-lg); }
		.header { display: flex; justify-content: space-between; align-items: center; padding: var(--clam-space-4) var(--clam-space-5); border-bottom: 1px solid var(--clam-border); flex-shrink: 0; }
		.header h3 { margin: 0; font-size: var(--clam-text-lg); color: var(--clam-text); }
		.close { background: none; border: none; font-size: 24px; cursor: pointer; color: var(--clam-text-muted); min-width: 40px; min-height: 40px; border-radius: var(--clam-radius-sm); display: flex; align-items: center; justify-content: center; }
		.close:hover { background: var(--clam-surface-soft); color: var(--clam-text); }
		.close:disabled { opacity: 0.4; cursor: not-allowed; }
		.body { padding: var(--clam-space-4) var(--clam-space-5); overflow-y: auto; flex: 1; }
		.stats { display: flex; gap: var(--clam-space-3); margin-bottom: var(--clam-space-3); flex-wrap: wrap; }
		.stat { background: var(--clam-surface-soft); border-radius: var(--clam-radius-md); padding: var(--clam-space-2) var(--clam-space-4); text-align: center; flex: 1 1 80px; min-width: 80px; }
		.stat-num { font-size: var(--clam-text-xl); font-weight: 700; line-height: 1.2; }
		.stat-label { font-size: var(--clam-text-xs); color: var(--clam-text-muted); text-transform: uppercase; }
		.stat-ok .stat-num { color: var(--clam-success); }
		.stat-err .stat-num { color: var(--clam-danger); }
		.stat-pend .stat-num { color: var(--clam-text-muted); }
		.progress-bar { width: 100%; height: 8px; background: var(--clam-border); border-radius: 4px; overflow: hidden; margin-bottom: var(--clam-space-4); }
		.progress-fill { height: 100%; background: var(--clam-primary); transition: width 0.3s ease; }
		.list { display: flex; flex-direction: column; gap: 2px; max-height: 40vh; overflow-y: auto; border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); padding: var(--clam-space-2); background: var(--clam-surface); }
		.row { display: flex; align-items: center; gap: var(--clam-space-3); padding: 6px 6px; border-radius: var(--clam-radius-sm); }
		.row-sending { background: #e8f4fd; }
		.icon { width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 14px; }
		.icon-pending { color: var(--clam-text-muted); }
		.icon-sending { color: var(--clam-primary); }
		.icon-ok { color: var(--clam-success); }
		.icon-err { color: var(--clam-danger); }
		.spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(90,157,30,0.25); border-top-color: var(--clam-primary); border-radius: 50%; animation: spin 0.8s linear infinite; }
		@keyframes spin { to { transform: rotate(360deg); } }
		.label { flex: 1; min-width: 0; font-size: var(--clam-text-sm); color: var(--clam-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
		.sublabel { font-size: var(--clam-text-xs); color: var(--clam-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 60%; }
		.row-error .sublabel { color: var(--clam-danger); white-space: normal; }
		.footer { display: flex; justify-content: space-between; align-items: center; padding: var(--clam-space-3) var(--clam-space-5); border-top: 1px solid var(--clam-border); gap: var(--clam-space-3); flex-shrink: 0; }
		.phase { font-size: var(--clam-text-sm); color: var(--clam-text-muted); }
		.actions { display: flex; gap: var(--clam-space-2); }
		.btn { padding: 8px var(--clam-space-4); border: none; border-radius: var(--clam-radius-md); font-size: var(--clam-text-sm); font-weight: 600; cursor: pointer; font-family: inherit; min-height: 36px; }
		.btn-primary { background: var(--clam-primary); color: var(--clam-on-primary); }
		.btn-primary:hover { background: var(--clam-primary-hover); }
		.btn-secondary { background: var(--clam-surface-soft); color: var(--clam-text); border: 1px solid var(--clam-border); }
		.btn-secondary:hover { background: var(--clam-border); }
		.btn:disabled { opacity: 0.5; cursor: not-allowed; }
		details { margin-top: var(--clam-space-3); }
		summary { cursor: pointer; font-size: var(--clam-text-sm); color: var(--clam-danger); font-weight: 600; }
		.err-list { margin-top: 6px; padding-left: 12px; font-size: var(--clam-text-xs); color: var(--clam-text-muted); max-height: 120px; overflow-y: auto; }
		.err-list div { padding: 2px 0; }
		@media (max-width: 480px) {
			.sublabel { display: none; }
			.label { white-space: normal; }
		}
	`;

	@property({attribute: false}) items: StepItem[] = [];
	@property({type: Boolean}) open = false;
	@property() title = 'Progreso';
	@property({type: Boolean}) cancellable = true;
	@property({attribute: false}) onChunk: ((ids: string[]) => Promise<StepChunkResult[]>) | null = null;
	@property({type: Number}) chunkSize = 25;

	@state() private _phase: StepPhase = 'idle';
	@state() private _itemStatus = new Map<string, ItemStatus>();
	@state() private _sent = 0;
	@state() private _failed = 0;
	@state() private _processed = 0;
	@state() private _failures: Array<{id: string; label: string; error: string}> = [];
	private _aborted = false;
	private _running = false;

	updated(changed: Map<string, unknown>): void {
		if (changed.has('open') && this.open && !this._running && this._phase === 'idle') {
			void this._run();
		}
	}

	private async _run(): Promise<void> {
		if (!this.onChunk || this.items.length === 0) {
			this._phase = 'done';
			this.requestUpdate();
			this._emitDone(true);
			return;
		}
		this._running = true;
		this._aborted = false;
		this._phase = 'running';
		this._itemStatus = new Map(this.items.map((it) => [it.id, 'pending']));
		this._sent = 0;
		this._failed = 0;
		this._processed = 0;
		this._failures = [];
		this.requestUpdate();

		const size = Math.max(1, this.chunkSize);
		for (let i = 0; i < this.items.length; i += size) {
			if (this._aborted) break;
			const chunk = this.items.slice(i, i + size);

			// marca sending
			const sending = new Map(this._itemStatus);
			for (const it of chunk) sending.set(it.id, 'sending');
			this._itemStatus = sending;
			this.requestUpdate();

			// ejecuta chunk
			let results: StepChunkResult[];
			try {
				results = await this.onChunk!(chunk.map((c) => c.id));
			} catch (e) {
				const msg = e instanceof Error ? e.message : String(e);
				results = chunk.map((c) => ({id: c.id, ok: false, error: msg}));
			}

			// distribuye resultados
			const next = new Map(this._itemStatus);
			for (const it of chunk) {
				const r = results.find((x) => x.id === it.id);
				if (r && r.ok) {
					next.set(it.id, 'ok');
					this._sent++;
				} else {
					next.set(it.id, 'err');
					this._failed++;
					this._failures = [...this._failures, {id: it.id, label: it.label, error: r?.error || 'Error desconocido'}];
				}
				this._processed++;
			}
			this._itemStatus = next;
			this.requestUpdate();
			// breathing room para que el spinner sea visible
			await new Promise((r) => setTimeout(r, 0));
		}

		this._phase = this._aborted ? 'cancelled' : 'done';
		this._running = false;
		this.requestUpdate();
		this._emitDone(this._aborted);
	}

	private _emitDone(cancelled: boolean): void {
		this.dispatchEvent(new CustomEvent<StepProgressDoneDetail>('clam-step-progress-done', {
			detail: {sent: this._sent, failed: this._failed, total: this.items.length, cancelled},
			bubbles: true,
			composed: true,
		}));
	}

	private _cancel(): void {
		this._aborted = true;
		this.requestUpdate();
	}

	private _close(): void {
		if (this._running) return;
		this.open = false;
		this._phase = 'idle';
		this._itemStatus = new Map();
		this._failures = [];
		this._sent = 0;
		this._failed = 0;
		this._processed = 0;
		this.requestUpdate();
		this.dispatchEvent(new CustomEvent('clam-step-progress-close', {bubbles: true, composed: true}));
	}

	render() {
		if (!this.open) return null;
		const total = this.items.length;
		const pct = total > 0 ? Math.round((this._processed / total) * 100) : 0;
		const phaseLabel = this._phase === 'running'
			? `Procesando… ${this._processed}/${total}`
			: this._phase === 'done'
				? `Completado · ${this._sent} enviados${this._failed > 0 ? `, ${this._failed} fallidos` : ''}`
				: this._phase === 'cancelled'
					? `Cancelado · ${this._sent} enviados, ${total - this._processed} sin procesar`
					: 'Iniciando…';
		return html`
			<div class="overlay">
				<div class="modal" role="dialog" aria-modal="true">
					<div class="header">
						<h3>${this.title}</h3>
						<button class="close" ?disabled=${this._running} @click=${() => this._close()} aria-label="Cerrar">&times;</button>
					</div>
					<div class="body">
						<div class="stats">
							<div class="stat stat-ok"><div class="stat-num">${this._sent}</div><div class="stat-label">Enviados</div></div>
							<div class="stat stat-err"><div class="stat-num">${this._failed}</div><div class="stat-label">Fallidos</div></div>
							<div class="stat stat-pend"><div class="stat-num">${Math.max(0, total - this._processed)}</div><div class="stat-label">Pendientes</div></div>
						</div>
						<div class="progress-bar" role="progressbar" aria-valuenow=${pct} aria-valuemin="0" aria-valuemax="100">
							<div class="progress-fill" style="width:${pct}%"></div>
						</div>
						<div class="list">
							${this.items.map((it) => {
								const status = this._itemStatus.get(it.id) || 'pending';
								const rowClass = status === 'sending' ? 'row row-sending' : status === 'err' ? 'row row-error' : 'row';
								const errInfo = status === 'err' ? this._failures.find((f) => f.id === it.id) : null;
								return html`
									<div class=${rowClass}>
										<span class="icon icon-${status}">
											${status === 'pending' ? html`•`
											: status === 'sending' ? html`<span class="spinner"></span>`
											: status === 'ok' ? iconCheck()
											: iconX()}
										</span>
										<div class="label">${it.label}</div>
										<div class="sublabel">${errInfo ? errInfo.error : (it.sublabel || '')}</div>
									</div>
								`;
							})}
						</div>
						${this._failures.length > 0 ? html`
							<details>
								<summary>Detalles de errores (${this._failures.length})</summary>
								<div class="err-list">
									${this._failures.map((f) => html`<div><strong>${f.label}:</strong> ${f.error}</div>`)}
								</div>
							</details>
						` : ''}
					</div>
					<div class="footer">
						<span class="phase">${phaseLabel}</span>
						<div class="actions">
							${this._phase === 'running' && this.cancellable
								? html`<button class="btn btn-secondary" @click=${() => this._cancel()} ?disabled=${this._aborted}>
										${this._aborted ? 'Cancelando…' : 'Cancelar'}
									</button>`
								: ''}
							${(this._phase === 'done' || this._phase === 'cancelled')
								? html`<button class="btn btn-primary" @click=${() => this._close()}>Cerrar</button>`
								: ''}
						</div>
					</div>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'clam-step-progress': ClamStepProgress;
	}
}
